import { z } from 'zod';
/**
 * Content schemas for GM Binder adventures.
 * Schemas are intentionally lenient (most fields optional, unknown keys allowed via
 * `.catchall`) so that hand-authored adventure JSON and migrated legacy data both validate.
 */
export const abilityScoresSchema = z
    .object({
    strength: z.number().optional(),
    dexterity: z.number().optional(),
    constitution: z.number().optional(),
    intelligence: z.number().optional(),
    wisdom: z.number().optional(),
    charisma: z.number().optional(),
})
    .catchall(z.unknown());
export const attackSchema = z
    .object({
    name: z.string(),
    bonus: z.string().optional(),
    damage: z.string().optional(),
    type: z.string().optional(),
    notes: z.string().optional(),
})
    .catchall(z.unknown());
export const spellSchema = z
    .object({
    name: z.string(),
    level: z.number().optional(),
    description: z.string().optional(),
})
    .catchall(z.unknown());
export const characterSchema = z
    .object({
    id: z.string().optional(),
    name: z.string(),
    race: z.string().optional(),
    class: z.string().optional(),
    classEnglish: z.string().optional(),
    level: z.number().optional(),
    background: z.string().optional(),
    alignment: z.string().optional(),
    description: z.string().optional(),
    personality: z.string().optional(),
    backstory: z.string().optional(),
    image: z.string().optional(),
    abilities: abilityScoresSchema.optional(),
    skills: z.array(z.string()).optional(),
    savingThrows: z.array(z.string()).optional(),
    languages: z.array(z.string()).optional(),
    equipment: z.array(z.string()).optional(),
    features: z.array(z.unknown()).optional(),
    traits: z.array(z.unknown()).optional(),
    attacks: z.array(attackSchema).optional(),
    spells: z.array(z.unknown()).optional(),
    spellcasting: z.unknown().optional(),
    hitPoints: z
        .object({
        max: z.number().optional(),
        current: z.number().optional(),
        hitDice: z.string().optional(),
    })
        .catchall(z.unknown())
        .optional(),
    armorClass: z.number().optional(),
    speed: z.number().optional(),
    adventureHook: z.string().optional(),
    personalityDetails: z.unknown().optional(),
    imagePrompt: z.string().optional(),
})
    .catchall(z.unknown());
export const floorSchema = z.object({
    id: z.string(),
    name: z.string(),
    order: z.number().default(0),
});
export const roomSchema = z
    .object({
    id: z.string(),
    name: z.string(),
    floor: z.string().optional(),
    icon: z.string().optional(),
    description: z.string().optional(),
    /** GM read-aloud "boxed text". */
    readAloud: z.string().optional(),
    /** Free-form detail/clue blocks shown in the room. */
    details: z.array(z.string()).optional(),
    /**
     * Legacy phase-gated "poltergeist" text. After the phase-system removal this is
     * surfaced as a normal highlighted detail block (an "event"/atmosphere note).
     */
    event: z.string().optional(),
    items: z.array(z.string()).optional(),
    clues: z.array(z.string()).optional(),
    connections: z.array(z.string()).optional(),
    secret: z.union([z.string(), z.boolean()]).optional(),
    npcs: z.array(z.string()).optional(),
    image: z.string().optional(),
    imagePrompt: z.string().optional(),
})
    .catchall(z.unknown());
export const roomsBlockSchema = z.object({
    floors: z.array(floorSchema).default([]),
    rooms: z.array(roomSchema).default([]),
    /** Optional section-level map image (media-relative path or URL). */
    mapImage: z.string().optional(),
    mapCaption: z.string().optional(),
});
export const npcSchema = z
    .object({
    id: z.string().optional(),
    name: z.string(),
    role: z.string().optional(),
    icon: z.string().optional(),
    status: z.string().optional(),
    /** Legacy alive/dead flag. */
    alive: z.boolean().optional(),
    description: z.string().optional(),
    stats: z.unknown().optional(),
    notes: z.string().optional(),
    image: z.string().optional(),
    imagePrompt: z.string().optional(),
})
    .catchall(z.unknown());
export const clueSchema = z
    .object({
    id: z.string().optional(),
    title: z.string(),
    author: z.string().optional(),
    icon: z.string().optional(),
    location: z.string().nullable().optional(),
    room: z.string().nullable().optional(),
    text: z.string().optional(),
})
    .catchall(z.unknown());
export const itemSchema = z
    .object({
    id: z.string().optional(),
    name: z.string(),
    icon: z.string().optional(),
    type: z.string().optional(),
    location: z.string().nullable().optional(),
    description: z.string().optional(),
    imagePrompt: z.string().optional(),
})
    .catchall(z.unknown());
export const monsterSchema = z
    .object({
    id: z.string().optional(),
    name: z.string(),
    icon: z.string().optional(),
    type: z.string().optional(),
    size: z.string().optional(),
    cr: z.union([z.string(), z.number()]).optional(),
    ac: z.union([z.string(), z.number()]).optional(),
    hp: z.union([z.string(), z.number()]).optional(),
    speed: z.string().optional(),
    stats: z
        .object({
        for: z.number().optional(),
        des: z.number().optional(),
        cos: z.number().optional(),
        int: z.number().optional(),
        sag: z.number().optional(),
        car: z.number().optional(),
    })
        .catchall(z.unknown())
        .optional(),
    senses: z.string().optional(),
    languages: z.string().optional(),
    damageImmunities: z.union([z.string(), z.array(z.string())]).optional(),
    damageResistances: z.union([z.string(), z.array(z.string())]).optional(),
    conditionImmunities: z.union([z.string(), z.array(z.string())]).optional(),
    actions: z.array(z.unknown()).optional(),
    notes: z.string().optional(),
})
    .catchall(z.unknown());
export const encounterSchema = z
    .object({
    id: z.string().optional(),
    name: z.string().optional(),
    room: z.string().nullable().optional(),
    difficulty: z.string().optional(),
    monsters: z
        .array(z.object({ id: z.string(), count: z.number().default(1) }).catchall(z.unknown()))
        .optional(),
    notes: z.string().optional(),
})
    .catchall(z.unknown());
export const curseSchema = z
    .object({
    roll: z.union([z.string(), z.number()]).optional(),
    name: z.string().optional(),
    rule: z.string().optional(),
})
    .catchall(z.unknown());
/** Optional "special mechanic" block (e.g. the party-curse d6 table). */
export const cursesBlockSchema = z
    .object({
    title: z.string().optional(),
    intro: z.string().optional(),
    description: z.string().optional(),
    curses: z.array(curseSchema).optional(),
})
    .catchall(z.unknown());
/** A custom free-form markdown page authored by the GM. */
export const pageSchema = z.object({
    id: z.string(),
    title: z.string(),
    icon: z.string().optional(),
    markdown: z.string().default(''),
});
//# sourceMappingURL=content.js.map